/* eslint-disable react/jsx-filename-extension */
import _ from 'underscore';
import React, { FC, useCallback, useState } from 'react';
import classnames from 'classnames';
import { Callout, Text, FontIcon } from '@fluentui/react';
import { Target } from '@fluentui/react-hooks';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { merge } from '@bingads-webui-universal/primitive-utilities';
import { statusStyle } from '@bingads-webui-campaign-react/common-styles';
import { createOrUpdateListingGroup } from '@bingads-webui-campaign-react/perf-max-data';
import { ListingGroupGridItemData, } from '../../interface';
import { fromViewModelToBulkModel } from '../../API/bulk';

interface IncludeExcludeTemplateProps {
  itemData: ListingGroupGridItemData
}

export const styles = (p, core) => {
  const { palette } = core;
  return {
    bold: {
      fontWeight: 600,
    },
    includeExcludeStatusContainer: {
      '& .include-exclude-status.disabled': {
        cursor: 'default',
      },
      '& .statusEnabled_icon.parent-node:before': {
        color: 'grey',
      },
    },
    includeExcludeCallout: merge(
      {
        marginTop: 10,
        marginLeft: 10,
        maxWidth: '90%',
        background: palette.white,
        '& .statuschangelink': {
          padding: '7px 20px',
          display: 'block',
          cursor: 'pointer',
        },
        '& .statuschange-icon': {
          marginRight: 5,
        },
        '& ul': {
          listStyle: 'none',
          paddingLeft: 0,
          marginBottom: 0,
          marginTop: 0,
          '& li': {
            cursor: 'pointer',
            display: 'list-item',
            '&:hover': {
              backgroundColor: palette.neutralLight,
            },
          },
          '& .ms-Icon': {
            paddingRight: 5,
          },
        },
      },
      statusStyle({ palette })
    ),
  };
};


export const IncludeExcludeTemplate: FC<IncludeExcludeTemplateProps> = (props) => {
  const styleClassNames: any = useDefaultStyles(styles);
  const {
    itemData,
  } = props;
  const [showCallout, setShowCallout] = useState<boolean>(false);
  const [target, setTarget] = useState<Target>(null);
  const statusIcon = !itemData.IsExcluded ? (
    <div>
      <div className={classnames('statusEnabled_icon', !itemData.IsLeaf ? 'parent-node' : '')} />
      {itemData.IsLeaf && <div className={classnames('iconba', 'iconba-ChevronDown')} />}
    </div>) : (
      <div>
        {itemData.IsLeaf && <FontIcon
          iconName="Blocked2"
        />}
        {!itemData.IsLeaf && <div className={classnames('statusEnabled_icon', 'parent-node')} />}
        {itemData.IsLeaf && <div className={classnames('iconba', 'iconba-ChevronDown')} />}
      </div>);

  const dismiss = useCallback(() => {
    setShowCallout(false);
  }, []);

  const show = useCallback((e) => {
    if (itemData.IsLeaf) {
      const cellEl = e.target.closest('.ms-DetailsRow-cell');
      setTarget(cellEl.getBoundingClientRect());
      setShowCallout(true);
    }
  }, [itemData.IsLeaf]);


  const onSave = useCallback((isInclude) => {
    const itemToAdd = _.extend(fromViewModelToBulkModel(itemData), {
      Id: -1,
      IsExcluded: !isInclude,
    });
    const itemToDelete = {
      Id: itemData.Id,
      AssetGroupId: itemData.AssetGroupId,
      Status: 'Deleted',
    };
    const res = createOrUpdateListingGroup([itemToAdd], [itemToDelete])
      .then(() => {
        itemData.refreshGridData();
      });

    return res;
  }, [itemData]);

  const onInclude = useCallback(() => {
    onSave(true);
  }, [onSave]);

  const onExclude = useCallback(() => {
    onSave(false);
  }, [onSave]);

  return (
    <div className={classnames(styleClassNames.includeExcludeStatusContainer)}>
      <div className={classnames('include-exclude-status', itemData.IsLeaf ? '' : 'disabled')} onClick={show} onKeyDown={show} role="button" tabIndex={0} aria-label={itemData.i18n.getString(_TL_('Status'))}>
        {statusIcon}
      </div>
      {showCallout && (
      <Callout
        role="dialog"
        gapSpace={0}
        coverTarget
        setInitialFocus
        directionalHint={0}
        isBeakVisible={false}
        onDismiss={dismiss}
        target={target}
        className={styleClassNames.includeExcludeCallout}
      >
        <ul role="menu">
          <li className="status-include">
            <span className="statuschangelink" tabIndex={0} role="menuitem" aria-label={itemData.i18n.getString(_TL_('Include this item'))} onClick={onInclude} onKeyDown={onInclude}>
              <span className={classnames('statuschange-icon', 'statusEnabled_icon')} />
              <Text className={itemData.IsExcluded ? null : styleClassNames.bold}>
                {itemData.i18n.getString(_TL_('Include'))}
              </Text>
            </span>
          </li>
          <li className="status-exclude">
            <span className="statuschangelink" tabIndex={0} role="menuitem" aria-label={itemData.i18n.getString(_TL_('Exclude this item'))} onClick={onExclude} onKeyDown={onExclude}>
              <FontIcon
                iconName="Blocked2"
              />
              <Text className={itemData.IsExcluded ? styleClassNames.bold : null}>
                {itemData.i18n.getString(_TL_('Exclude'))}
              </Text>
            </span>
          </li>
        </ul>
      </Callout>)}
    </div>);
};
